/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/core";
import {
  Typography,
  Box,
  TextField,
  Button,
  Tooltip,
  Link,
} from "@material-ui/core";
import React, { FC, SVGAttributes, useEffect, useRef } from "react";
import { PortalChatService } from "@spwn-portal/grpc";
import { useConnectClient } from "utility/connectWeb";

import { AnnouncementIcon } from "./AnnouncementIcon";
import { Store, useSelector } from "store";

export const StreamingAnnouncement: React.FC<{
  eventId: string;
  currentVideoId: string;
  streamingType: "Live" | "VOD";
  isOpenAnnouncement: boolean;
  isEditingAnnouncement: boolean;
  setIsEditingAnnouncement: (isEditing: boolean) => void;
  streamingAnnouncement: string;
  setStreamingAnnouncement: (announcement: string) => void;
}> = ({
  eventId,
  currentVideoId,
  streamingType,
  isOpenAnnouncement,
  isEditingAnnouncement,
  setIsEditingAnnouncement: setIsEditing,
  streamingAnnouncement,
  setStreamingAnnouncement,
}) => {
  const connectClient = useConnectClient(PortalChatService);
  const isAdmin = useSelector((state: Store) => state.admin.isAdmin);
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );

  if (streamingType === "VOD") {
    return null;
  }

  if (!isAdmin && streamingAnnouncement === "") {
    return null;
  }

  if (!isOpenAnnouncement) {
    return null;
  }

  if (isEditingAnnouncement) {
    return (
      <StreamingAnnouncementLayout
        isDarkModeEnabled={isDarkModeEnabled}
        gridTemplateColumns={"1fr"}
        onClickOutside={() => setIsEditing(false)}
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent={"flex-end"}
          gridGap="4px"
        >
          <Button
            variant="outlined"
            onClick={() => setIsEditing(false)}
            style={{
              width: "88px",
              padding: "6px 12px",
              color: isDarkModeEnabled ? "#FFFFFF" : "#000000",
              fontSize: "12px",
              lineHeight: "1",
              backgroundColor: "inherit",
              border: "none",
            }}
          >
            キャンセル
          </Button>

          <Button
            variant="contained"
            onClick={async () => {
              setIsEditing(false);
              await connectClient.saveStreamingAnnouncement({
                videoId: currentVideoId,
                message: streamingAnnouncement,
                eventId,
              });
            }}
            style={{
              width: "88px",
              padding: "10px 12px",
              color: isDarkModeEnabled ? "#000000" : "#FFFFFF",
              fontSize: "12px",
              lineHeight: "1",
              backgroundColor: isDarkModeEnabled ? "#fff" : "#000",
              boxShadow: "none",
            }}
          >
            保存する
          </Button>
        </Box>

        <Box display="grid" gridTemplateColumns="1fr" gridGap="8px">
          <TextField
            value={streamingAnnouncement}
            onChange={(e) => {
              setStreamingAnnouncement(e.target.value);
            }}
            autoFocus
            multiline
            InputProps={{
              disableUnderline: true,
              style: {
                padding: "8px 10px",
                color: isDarkModeEnabled ? "#FFFFFF" : "#000000",
                fontSize: "12px",
                fontWeight: "bold",
                lineHeight: "1.5",
              },
            }}
            style={{
              width: "100%",
              borderRadius: "4px",
              whiteSpace: "pre-wrap",
            }}
            css={css`
              .MuiInput-formControl {
                padding: 0 !important;
              }
            `}
          />
        </Box>
      </StreamingAnnouncementLayout>
    );
  }

  return (
    <Tooltip title={isAdmin ? "アナウンスを編集" : ""} placement="top" arrow>
      <StreamingAnnouncementLayout
        isDarkModeEnabled={isDarkModeEnabled}
        gridTemplateColumns={"1fr"}
      >
        <StreamingAnnouncementHeading
          isAdmin={isAdmin}
          isDarkModeEnabled={isDarkModeEnabled}
          onClickIcon={() => setIsEditing(true)}
        />

        <Typography
          variant="subtitle1"
          onClick={() => isAdmin && setIsEditing(true)}
          style={{
            width: "100%",
            ...(streamingAnnouncement === "" && {
              color: "#A3A3A3",
            }),
            fontSize: "12px",
            fontWeight: "bold",
            lineHeight: "1.5",
            whiteSpace: "pre-wrap",
            ...(isAdmin && { cursor: "pointer" }),
          }}
        >
          {streamingAnnouncement === "" ? (
            "アナウンスを入力"
          ) : (
            <FormattedTextWithLink text={streamingAnnouncement} />
          )}
        </Typography>
      </StreamingAnnouncementLayout>
    </Tooltip>
  );
};

const StreamingAnnouncementLayout: FC<{
  children: React.ReactNode;
  isDarkModeEnabled: boolean;
  gridTemplateColumns: string;
  onClickOutside?: () => void;
}> = ({ children, isDarkModeEnabled, gridTemplateColumns, onClickOutside }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClickOutside?.();
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [onClickOutside]);

  return (
    <Box
      // @ts-expect-error TS2769
      ref={ref}
      display={"grid"}
      gridTemplateColumns={gridTemplateColumns}
      gridGap={"8px"}
      padding={"12px 16px"}
      color={isDarkModeEnabled ? "dark.icon.primary" : "light.icon.primary"}
      borderColor={isDarkModeEnabled ? "#333333" : "#F0F0F0"}
      border={"1px solid"}
      borderRadius={"12px"}
    >
      {children}
    </Box>
  );
};

const StreamingAnnouncementHeading: FC<{
  isAdmin: boolean;
  isDarkModeEnabled: boolean;
  onClickIcon: () => void;
}> = ({ isAdmin, isDarkModeEnabled, onClickIcon }) => {
  return (
    <Box display="flex" alignItems="center" justifyContent={"space-between"}>
      <Box display="flex" alignItems="center" gridGap="4px">
        <AnnouncementIcon
          style={{
            width: "18px",
            height: "auto",
          }}
        />
        <Box fontSize={"14px"} fontWeight={"bold"} lineHeight={"1"}>
          アナウンス
        </Box>
      </Box>

      {isAdmin && (
        <PenIcon
          width={"20px"}
          height={"20px"}
          color={isDarkModeEnabled ? "dark.icon.primary" : "light.icon.primary"}
          style={{ cursor: "pointer" }}
          onClick={onClickIcon}
        />
      )}
    </Box>
  );
};

interface FormattedTextWithLinkProps {
  text: string;
}
const FormattedTextWithLink: React.FC<FormattedTextWithLinkProps> = ({
  text,
}) => {
  // より包括的なURLを検出する正規表現
  const urlRegex = /(https?:\/\/(?:www\.)?[^\s<>[\]{}|\\^`]+)/gi;

  // テキストをURLで分割
  const parts: string[] = text.split(urlRegex);

  return (
    <React.Fragment>
      {parts.map((part: string, index: number) => {
        // URLかどうかをチェック
        if (urlRegex.test(part)) {
          return (
            <Link
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              underline="hover"
              title={part} // ホバー時に完全なURLを表示
              style={{ color: "#3182ce" }}
            >
              {part}
            </Link>
          );
        }
        return part;
      })}
    </React.Fragment>
  );
};

const PenIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 20 20" {...props}>
      <path d="M2.5 14.3751V17.5001H5.625L14.8417 8.28342L11.7167 5.15842L2.5 14.3751ZM17.2583 5.86675C17.3356 5.78966 17.3969 5.69808 17.4387 5.59727C17.4805 5.49646 17.502 5.38839 17.502 5.27925C17.502 5.17011 17.4805 5.06204 17.4387 4.96123C17.3969 4.86042 17.3356 4.76885 17.2583 4.69175L15.3083 2.74175C15.2312 2.6645 15.1397 2.60321 15.0389 2.56139C14.938 2.51957 14.83 2.49805 14.7208 2.49805C14.6117 2.49805 14.5036 2.51957 14.4028 2.56139C14.302 2.60321 14.2104 2.6645 14.1333 2.74175L12.6083 4.26675L15.7333 7.39175L17.2583 5.86675Z" />
    </svg>
  );
};
